import React, { useState } from 'react';
// Native MUI Components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { Apartment, ApartmentType } from '@/types';
import { apartmentService } from '@/services/apartmentService';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';

interface AddApartmentFormProps {
  tripId: string;
  apartmentTypes: ApartmentType[];
  trigger?: React.ReactNode;
}

const AddApartmentForm: React.FC<AddApartmentFormProps> = ({ tripId, apartmentTypes, trigger }) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: '',
    class: 'standard' as 'economy' | 'standard' | 'premium',
    maxOccupancy: 2,
    apartmentTypeId: '',
    amenities: ['']
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || formData.maxOccupancy <= 0 || !formData.apartmentTypeId) {
      return;
    }

    try {
      await apartmentService.addApartment(tripId, {
        ...formData,
        currentOccupancy: 0,
        amenities: formData.amenities.filter(amenity => amenity.trim() !== '')
      });

      toast({
        title: t('apartment.form.apartmentAdded'),
        description: t('apartment.form.apartmentAddedDescription', { name: formData.name }),
      });

      // Reset form
      setFormData({
        name: '',
        class: 'standard',
        maxOccupancy: 2,
        apartmentTypeId: '',
        amenities: ['']
      });

      setOpen(false);
    } catch (error) {
      toast({
        title: t('apartment.form.errorAddingApartment'),
        description: t('apartment.form.errorAddingApartmentDescription'),
        variant: "destructive",
      });
    }
  };

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAmenityChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.map((amenity, i) => i === index ? value : amenity)
    }));
  };

  const addAmenity = () => {
    setFormData(prev => ({
      ...prev,
      amenities: [...prev.amenities, '']
    }));
  };

  const removeAmenity = (index: number) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  return (
    <>
      {trigger ? (
        <Box onClick={() => setOpen(true)} sx={{ cursor: 'pointer' }}>
          {trigger}
        </Box>
      ) : (
        <Button 
          variant="contained" 
          onClick={() => setOpen(true)}
          startIcon={<Add />}
          sx={{ bgcolor: 'purple.600', '&:hover': { bgcolor: 'purple.700' } }}
        >
          {t('apartment.form.addApartment')}
        </Button>
      )}
      
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Typography variant="h6" component="div">
            {t('apartment.form.addNewApartment')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              color: 'grey.500',
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('apartment.form.apartmentName')}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder={t('apartment.form.apartmentNamePlaceholder')}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('apartment.form.apartmentClass')}</InputLabel>
                  <Select
                    value={formData.class}
                    onChange={(e) => handleChange('class', e.target.value)}
                    label={t('apartment.form.apartmentClass')}
                  >
                    <MenuItem value="economy">{t('apartment.form.classEconomy')}</MenuItem>
                    <MenuItem value="standard">{t('apartment.form.classStandard')}</MenuItem>
                    <MenuItem value="premium">{t('apartment.form.classPremium')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('apartment.form.maxOccupancy')}
                  type="number"
                  inputProps={{ min: 1 }}
                  value={formData.maxOccupancy}
                  onChange={(e) => handleChange('maxOccupancy', parseInt(e.target.value) || 0)}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth required>
                  <InputLabel>{t('apartment.form.apartmentType')}</InputLabel>
                  <Select
                    value={formData.apartmentTypeId}
                    onChange={(e) => handleChange('apartmentTypeId', e.target.value)}
                    label={t('apartment.form.apartmentType')}
                  >
                    {apartmentTypes.map((apartmentType) => (
                      <MenuItem key={apartmentType.id} value={apartmentType.id}>
                        {apartmentType.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    {t('apartment.form.amenities')}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={addAmenity}
                    startIcon={<Add />}
                  >
                    {t('apartment.form.addAmenity')}
                  </Button>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {formData.amenities.map((amenity, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        value={amenity}
                        onChange={(e) => handleAmenityChange(index, e.target.value)}
                        placeholder={t('apartment.form.amenityPlaceholder')}
                      />
                      {formData.amenities.length > 1 && (
                        <IconButton
                          onClick={() => removeAmenity(index)}
                          size="small"
                          color="error"
                        >
                          <Close />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOpen(false)} 
            variant="outlined"
          >
            {t('apartment.form.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
          >
            {t('apartment.form.addApartment')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddApartmentForm;
